import React, { useEffect, useState } from 'react';
import { Alert, Badge, ListGroup } from 'react-bootstrap';
import Loading from '../../components/Loading';
import { getSecurityActivity, SecurityEvent } from '../../api/auth/actions';

interface SecurityActivityProps {
  dialogId?: string;
}

// Friendly labels for the audit event types emitted by the backend.
const EVENT_LABELS: Record<string, string> = {
  password_otp_requested: 'Password change code requested',
  password_otp_verified: 'Identity verified for password change',
  password_otp_failed: 'Failed verification code attempt',
  password_changed: 'Password changed',
  password_change_failed: 'Failed password change attempt',
  login_failed: 'Failed sign-in attempt',
  account_blocked: 'Account blocked after failed attempts',
};

const eventLabel = (event: string): string =>
  EVENT_LABELS[event] || event.replace(/_/g, ' ');

const formatTimestamp = (unixSeconds: number): string => {
  if (!unixSeconds) return '';
  return new Date(unixSeconds * 1000).toLocaleString();
};

const SecurityActivity: React.FC<SecurityActivityProps> = () => {
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getSecurityActivity();
      setEvents(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load security activity.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="text-center my-3">
        <Loading />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (events.length === 0) {
    return <p className="text-muted mb-0">No recent security activity.</p>;
  }

  return (
    <>
      <p className="text-muted">Recent security events on your account, newest first.</p>
      <ListGroup variant="flush">
        {events.map((evt, idx) => (
          <ListGroup.Item key={idx} className="px-0">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="fw-semibold">{eventLabel(evt.event)}</div>
                <small className="text-muted">
                  {formatTimestamp(evt.timestamp)}
                  {evt.ip ? ` · ${evt.ip}` : ''}
                </small>
              </div>
              <Badge bg={evt.outcome === 'success' ? 'success' : 'danger'} pill>
                {evt.outcome}
              </Badge>
            </div>
          </ListGroup.Item>
        ))}
      </ListGroup>
    </>
  );
};

export default SecurityActivity;
